import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  Alert,
} from 'react-native';
import type { CareRole } from '../services/unifiedAuthService';
import type { User } from '../services/unifiedAuthService';
import type { CareMedication, CareTask, CareAlert, CareCircleMember } from '../types/care';
import { careMedicationService } from '../services/careMedicationService';
import { careTaskService } from '../services/careTaskService';
import { careCircleService } from '../services/careCircleService';
import { UnifiedAuthService } from '../services/unifiedAuthService';
import {
  getCareRecipientId,
  getCareRecipientUser,
  getUpcomingAppointmentsForRecipient,
  buildCareAlerts,
} from '../services/careDashboardService';
import type { Appointment } from '../utils/appointmentManager';
import CareAddMedicationModal from '../components/CareAddMedicationModal';
import CareAddTaskModal from '../components/CareAddTaskModal';
import CareTeamModal from '../components/CareTeamModal';

interface CareDashboardScreenProps {
  careRole: CareRole;
  /** If not provided, dashboard will fetch current user on mount. */
  currentUser?: User | null;
  onNavigateToSettings?: () => void;
  /** Navigate to Account / Profile (for Delete account, etc.). */
  onNavigateToAccount?: () => void;
  onLogout?: () => void;
  /** Navigate to the booking flow (find businesses, book appointment). */
  onNavigateToBookAppointment?: () => void;
  /** Open notes & incident log for this recipient. */
  onNavigateToNotes?: (recipientId: string, recipientName: string, canEdit: boolean) => void;
}

const CARE_ROLE_LABELS: Record<CareRole, string> = {
  family: 'Family / Coordinator',
  senior: 'Senior / Care Recipient',
  professional: 'Care Professional',
};

const CareDashboardScreen: React.FC<CareDashboardScreenProps> = ({
  careRole,
  currentUser: currentUserProp,
  onNavigateToSettings,
  onNavigateToAccount,
  onLogout,
  onNavigateToBookAppointment,
  onNavigateToNotes,
}) => {
  const [currentUser, setCurrentUser] = useState<User | null>(currentUserProp ?? null);
  const [recipientId, setRecipientId] = useState<string | null>(null);
  const [recipientName, setRecipientName] = useState<string>('');
  const [medications, setMedications] = useState<CareMedication[]>([]);
  const [tasks, setTasks] = useState<CareTask[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [alerts, setAlerts] = useState<CareAlert[]>([]);
  const [careMembers, setCareMembers] = useState<CareCircleMember[]>([]);
  const [myMember, setMyMember] = useState<CareCircleMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [addMedVisible, setAddMedVisible] = useState(false);
  const [addTaskVisible, setAddTaskVisible] = useState(false);
  const [careTeamVisible, setCareTeamVisible] = useState(false);

  const todayDate = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (currentUserProp != null) setCurrentUser(currentUserProp);
    else UnifiedAuthService.getCurrentUser().then(setCurrentUser);
  }, [currentUserProp]);

  const load = useCallback(async () => {
    const user = currentUserProp ?? currentUser;
    if (!user) {
      setLoading(false);
      setRefreshing(false);
      return;
    }
    const rid = getCareRecipientId(user, careRole);
    setRecipientId(rid);
    if (!rid) {
      setMedications([]);
      setTasks([]);
      setAppointments([]);
      setAlerts([]);
      setRecipientName('');
      setLoading(false);
      setRefreshing(false);
      return;
    }
    try {
      const [recipientUser, meds, taskList, members] = await Promise.all([
        getCareRecipientUser(rid),
        careMedicationService.list(rid),
        careTaskService.list(rid),
        careCircleService.getMembers(rid),
      ]);
      if (recipientUser) setRecipientName(recipientUser.name || 'Care recipient');
      setMedications(meds);
      setTasks(taskList);
      setCareMembers(members);
      const me = members.find((m) => m.userId === user.id) ?? null;
      setMyMember(me);
      const apts = recipientUser
        ? await getUpcomingAppointmentsForRecipient(
            recipientUser.email,
            recipientUser.name,
            recipientUser.email
          )
        : [];
      setAppointments(apts);
      setAlerts(buildCareAlerts(meds, taskList, todayDate));
    } catch (e) {
      console.warn('Care dashboard load error:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentUser, currentUserProp, careRole, todayDate]);

  useEffect(() => {
    load();
  }, [load]);

  const onRefresh = () => {
    setRefreshing(true);
    load();
  };

  const handleAddMed = (med: Omit<CareMedication, 'id' | 'createdAt'>) => {
    if (!recipientId) return;
    careMedicationService.add(recipientId, med).then(() => load());
  };

  const handleMarkTaken = (medId: string) => {
    if (!recipientId) return;
    careMedicationService.markTaken(recipientId, medId).then(() => load());
  };

  const handleMarkTaskDone = (taskId: string) => {
    if (!recipientId) return;
    careTaskService.markDone(recipientId, taskId).then(() => load());
  };

  const handleAddTask = (task: Omit<CareTask, 'id' | 'createdAt' | 'status'>) => {
    if (!recipientId) return;
    careTaskService.add(recipientId, task).then(() => load());
  };

  if (!currentUser) {
    return (
      <View style={styles.center}>
        <Text style={styles.subtitle}>Loading...</Text>
      </View>
    );
  }

  if (loading && !refreshing) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.subtitle}>Loading care dashboard...</Text>
      </View>
    );
  }

  const rid = getCareRecipientId(currentUser, careRole);
  if (!rid) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Care Coordination</Text>
          <Text style={styles.subtitle}>{CARE_ROLE_LABELS[careRole]}</Text>
        </View>
        <View style={styles.emptyState}>
          <Text style={styles.emptyTitle}>Link to someone you care for</Text>
          <Text style={styles.emptyText}>
            To see medications, tasks, and appointments, link your account to a care recipient. This can be set up in Settings or by inviting them to join.
          </Text>
          {onNavigateToSettings && (
            <TouchableOpacity style={styles.primaryButton} onPress={onNavigateToSettings}>
              <Text style={styles.primaryButtonText}>Open Settings</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  }

  const todaysMeds = careMedicationService.getTodaysMeds(medications, todayDate);
  const upcomingTasks = careTaskService.getUpcoming(tasks, todayDate, 7);
  const canViewContent = true; // All roles see full dashboard; add buttons gated by canEdit/canAssignTasks
  const canEdit = careRole === 'senior' || (myMember && careCircleService.canEdit(myMember));
  const canMarkTasksDone = careRole === 'senior' || (myMember && careCircleService.canMarkTasksDone(myMember, undefined, currentUser?.id));
  const canAssignTasks = myMember && careCircleService.canAssignTasks(myMember);
  const canManageTeam = careRole === 'senior' || (myMember && (myMember.role === 'coordinator' || careCircleService.canEdit(myMember)));

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Care Coordination</Text>
          <>
            <Text style={styles.subtitle}>Care Recipient: {recipientName || 'Care recipient'}</Text>
            <Text style={styles.subtitle}>Your role: {CARE_ROLE_LABELS[careRole]}</Text>
          </>
        </View>
        <View style={styles.headerActions}>
          {onNavigateToAccount && (
            <TouchableOpacity onPress={onNavigateToAccount} style={styles.headerActionButton}>
              <Text style={styles.headerActionText}>Account</Text>
            </TouchableOpacity>
          )}
          {onLogout && (
            <TouchableOpacity onPress={onLogout}>
              <Text style={styles.logoutText}>Log out</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#007AFF" />
        }
      >
        {/* Alerts */}
        {alerts.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Alerts</Text>
            {alerts.map((a) => (
              <View key={a.id} style={styles.alertRow}>
                <Text style={styles.alertIcon}>⚠</Text>
                <View style={styles.alertBody}>
                  <Text style={styles.alertTitle}>{a.title}</Text>
                  <Text style={styles.alertMessage}>{a.message}</Text>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Today's meds */}
        <View style={styles.section}>
          <View style={styles.sectionRow}>
            <Text style={styles.sectionTitle}>Today&apos;s medications</Text>
            {(canEdit || canAssignTasks) && (
              <TouchableOpacity onPress={() => setAddMedVisible(true)} style={styles.addButton}>
                <Text style={styles.addButtonText}>+ Add</Text>
              </TouchableOpacity>
            )}
          </View>
          {todaysMeds.length === 0 ? (
            <Text style={styles.emptySection}>No medications added yet. Tap &quot;+ Add&quot; to track compliance.</Text>
          ) : (
            todaysMeds.map((m) => {
              const taken = careMedicationService.isTakenToday(m, todayDate);
              const missed = careMedicationService.isMissedToday(m, todayDate);
              return (
                <View key={m.id} style={styles.medRow}>
                  <View style={styles.medLeft}>
                    <Text style={styles.medName}>{m.name}</Text>
                    <Text style={styles.medSchedule}>{m.scheduleLabel}</Text>
                  </View>
                  <View style={styles.medRight}>
                    {taken ? (
                      <Text style={styles.statusDone}>✓ Done</Text>
                    ) : missed ? (
                      <Text style={styles.statusMissed}>⚠ Missed</Text>
                    ) : canMarkTasksDone ? (
                      <TouchableOpacity
                        style={styles.markTakenBtn}
                        onPress={() => handleMarkTaken(m.id)}
                      >
                        <Text style={styles.markTakenBtnText}>Mark taken</Text>
                      </TouchableOpacity>
                    ) : null}
                  </View>
                </View>
              );
            })
          )}
        </View>

        {/* Upcoming appointments */}
        <View style={styles.section}>
          <View style={styles.sectionRow}>
            <Text style={styles.sectionTitle}>Upcoming appointments</Text>
            {onNavigateToBookAppointment && (
              <TouchableOpacity onPress={onNavigateToBookAppointment} style={styles.bookBtnBlue}>
                <Text style={styles.bookBtnBlueText}>Book</Text>
              </TouchableOpacity>
            )}
          </View>
          {appointments.length === 0 ? (
            <Text style={styles.emptySection}>No upcoming appointments.</Text>
          ) : (
            appointments.slice(0, 5).map((apt) => (
              <View key={apt.id} style={styles.aptRow}>
                <Text style={styles.aptService}>{apt.serviceType}</Text>
                <Text style={styles.aptMeta}>
                  {apt.appointmentDate} {apt.appointmentTime || ''} • {apt.businessName || '—'}
                </Text>
              </View>
            ))
          )}
        </View>

        {/* Care team */}
        {canManageTeam && (
          <View style={styles.section}>
            <View style={styles.sectionRow}>
              <Text style={styles.sectionTitle}>Care team</Text>
              <TouchableOpacity onPress={() => setCareTeamVisible(true)}>
                <Text style={styles.link}>Manage</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.emptySection}>Add siblings and caregivers. Control view, edit, mark tasks done.</Text>
          </View>
        )}

        {/* Assigned tasks */}
        <View style={styles.section}>
          <View style={styles.sectionRow}>
            <Text style={styles.sectionTitle}>Assigned tasks</Text>
            {(canEdit || canAssignTasks) && (
              <TouchableOpacity onPress={() => setAddTaskVisible(true)} style={styles.addButton}>
                <Text style={styles.addButtonText}>+ Add</Text>
              </TouchableOpacity>
            )}
          </View>
          {upcomingTasks.length === 0 ? (
            <Text style={styles.emptySection}>
              No tasks due soon. Tap &quot;+ Add&quot; to add a task and assign to a family member.
            </Text>
          ) : (
            upcomingTasks.map((t) => (
              <View key={t.id} style={styles.taskRow}>
                <View style={styles.taskLeft}>
                  <Text style={styles.taskTitle}>{t.title}</Text>
                  <Text style={styles.taskDue}>Due {t.dueDate}{t.dueTime ? ` ${t.dueTime}` : ''}</Text>
                </View>
                {t.status === 'pending' ? (
                  canMarkTasksDone || (t.assignedToUserId && t.assignedToUserId === currentUser?.id) ? (
                    <TouchableOpacity
                      style={styles.markDoneBtn}
                      onPress={() => handleMarkTaskDone(t.id)}
                    >
                      <Text style={styles.markDoneBtnText}>✓ Done</Text>
                    </TouchableOpacity>
                  ) : null
                ) : t.status === 'done' ? (
                  <Text style={styles.statusDone}>✓ Done</Text>
                ) : (
                  <Text style={styles.statusMissed}>⚠ Missed</Text>
                )}
              </View>
            ))
          )}
        </View>

        {/* Notes & incidents */}
        {onNavigateToNotes && (
          <View style={styles.section}>
            <TouchableOpacity onPress={() => onNavigateToNotes(recipientId!, recipientName || 'Recipient', canEdit)}>
              <Text style={styles.sectionTitle}>Notes & incidents</Text>
              <Text style={styles.emptySection}>Timestamped notes. Searchable. Export to PDF.</Text>
            </TouchableOpacity>
          </View>
        )}

        <View style={styles.footerNote}>
          <Text style={styles.footerText}>
            Medication tracking is for coordination only — no dosage or medical advice. Reminders can be sent by push or SMS; missed doses can alert family.
          </Text>
        </View>
      </ScrollView>

      <CareAddMedicationModal
        visible={addMedVisible}
        onClose={() => setAddMedVisible(false)}
        onSave={handleAddMed}
      />
      <CareAddTaskModal
        visible={addTaskVisible}
        onClose={() => setAddTaskVisible(false)}
        onSave={handleAddTask}
        careMembers={canAssignTasks ? careMembers : undefined}
      />
      {recipientId && (
        <CareTeamModal
          visible={careTeamVisible}
          recipientId={recipientId}
          recipientName={recipientName || undefined}
          inviterName={currentUser?.name || undefined}
          onClose={() => setCareTeamVisible(false)}
          onUpdated={load}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F2F2F7' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F2F2F7' },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fff',
    paddingHorizontal: 20,
    paddingTop: 56,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E7',
  },
  title: { fontSize: 22, fontWeight: '700', color: '#1C1C1E' },
  subtitle: { fontSize: 14, color: '#8E8E93', marginTop: 2 },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  bookBtnBlue: { backgroundColor: '#007AFF', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8 },
  bookBtnBlueText: { fontSize: 14, fontWeight: '600', color: '#fff' },
  addButton: { backgroundColor: '#007AFF', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8 },
  addButtonText: { fontSize: 14, fontWeight: '600', color: '#fff' },
  headerActionButton: { marginRight: 4 },
  headerActionText: { fontSize: 15, color: '#007AFF', fontWeight: '500' },
  logoutText: { fontSize: 15, color: '#007AFF', fontWeight: '500' },
  scroll: { flex: 1 },
  scrollContent: { padding: 16, paddingBottom: 40 },
  section: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  sectionRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  sectionTitle: { fontSize: 17, fontWeight: '600', color: '#1C1C1E', marginBottom: 12 },
  link: { fontSize: 15, color: '#007AFF', fontWeight: '500' },
  emptySection: { fontSize: 14, color: '#8E8E93', fontStyle: 'italic' },
  alertRow: {
    flexDirection: 'row',
    backgroundColor: '#FFF3E0',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  alertIcon: { fontSize: 18, marginRight: 8 },
  alertBody: { flex: 1 },
  alertTitle: { fontSize: 15, fontWeight: '600', color: '#1C1C1E' },
  alertMessage: { fontSize: 14, color: '#666', marginTop: 2 },
  medRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F2F2F7',
  },
  medLeft: { flex: 1 },
  medName: { fontSize: 16, fontWeight: '600', color: '#1C1C1E' },
  medSchedule: { fontSize: 13, color: '#8E8E93', marginTop: 2 },
  medRight: { minWidth: 100, alignItems: 'flex-end' },
  statusDone: { fontSize: 14, fontWeight: '600', color: '#34C759' },
  statusMissed: { fontSize: 14, fontWeight: '600', color: '#FF9500' },
  markTakenBtn: { backgroundColor: '#007AFF', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8 },
  markTakenBtnText: { fontSize: 13, fontWeight: '600', color: '#fff' },
  aptRow: { paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F2F2F7' },
  aptService: { fontSize: 15, fontWeight: '600', color: '#1C1C1E' },
  aptMeta: { fontSize: 13, color: '#8E8E93', marginTop: 2 },
  taskRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F2F2F7',
  },
  taskLeft: { flex: 1 },
  taskTitle: { fontSize: 15, fontWeight: '600', color: '#1C1C1E' },
  taskDue: { fontSize: 13, color: '#8E8E93', marginTop: 2 },
  markDoneBtn: { backgroundColor: '#34C759', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8 },
  markDoneBtnText: { fontSize: 13, fontWeight: '600', color: '#fff' },
  emptyState: { flex: 1, padding: 24, justifyContent: 'center' },
  emptyTitle: { fontSize: 18, fontWeight: '700', color: '#1C1C1E', marginBottom: 8 },
  emptyText: { fontSize: 15, color: '#8E8E93', lineHeight: 22, marginBottom: 20 },
  primaryButton: { backgroundColor: '#007AFF', padding: 14, borderRadius: 10, alignItems: 'center' },
  primaryButtonText: { fontSize: 16, fontWeight: '600', color: '#fff' },
  footerNote: { paddingHorizontal: 4, paddingTop: 8 },
  footerText: { fontSize: 12, color: '#8E8E93', fontStyle: 'italic' },
});

export default CareDashboardScreen;
